/**
 * Filter Bar Component
 * Provides filtering controls for excessive access data:
 * - Classification dropdown
 * - Risk score range slider
 * - Department dropdown
 * - Clear filters button
 */

import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Filter, X } from 'lucide-react';

const FilterBar = ({ filters, onFilterChange, onClearFilters, departments = [] }) => {
  const handleClassificationChange = (e) => {
    onFilterChange({ classification: e.target.value === 'all' ? null : e.target.value });
  };

  const handleDepartmentChange = (e) => {
    onFilterChange({ department: e.target.value === 'all' ? null : e.target.value });
  };

  const handleRiskScoreChange = (field, value) => {
    onFilterChange({ [field]: value || null });
  };

  const hasActiveFilters = () => {
    return filters.classification ||
           filters.department !== 'all' ||
           filters.minRiskScore !== null ||
           filters.maxRiskScore !== null;
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Filter size={20} style={{ marginRight: 8 }} />
          <Typography variant="h6" sx={{ mb: 0 }}>Filters</Typography>
        </Box>

        <Grid2 container spacing={2}>
          <Grid2 xs={12} md={3}>
            <FormControl fullWidth size="small" sx={{ mb: { xs: 2, md: 0 } }}>
              <InputLabel>Classification</InputLabel>
              <Select
                value={filters.classification || 'all'}
                onChange={handleClassificationChange}
                label="Classification"
              >
                <MenuItem value="all">All Classifications</MenuItem>
                <MenuItem value="CRITICAL_EXCESS">Critical Excess</MenuItem>
                <MenuItem value="HIGH_EXCESS">High Excess</MenuItem>
                <MenuItem value="MODERATE_EXCESS">Moderate Excess</MenuItem>
                <MenuItem value="ACCEPTABLE">Acceptable</MenuItem>
              </Select>
            </FormControl>
          </Grid2>

          <Grid2 xs={12} md={3}>
            <FormControl fullWidth size="small" sx={{ mb: { xs: 2, md: 0 } }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={filters.department || 'all'}
                onChange={handleDepartmentChange}
                label="Department"
              >
                <MenuItem value="all">All Departments</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>

          <Grid2 xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Min Risk Score"
              inputProps={{ min: 1, max: 10 }}
              placeholder="1"
              value={filters.minRiskScore || ''}
              onChange={(e) => handleRiskScoreChange('minRiskScore', e.target.value)}
              sx={{ mb: { xs: 2, md: 0 } }}
            />
          </Grid2>

          <Grid2 xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Risk Score"
              inputProps={{ min: 1, max: 10 }}
              placeholder="10"
              value={filters.maxRiskScore || ''}
              onChange={(e) => handleRiskScoreChange('maxRiskScore', e.target.value)}
              sx={{ mb: { xs: 2, md: 0 } }}
            />
          </Grid2>

          <Grid2 xs={12} md={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              onClick={onClearFilters}
              disabled={!hasActiveFilters()}
              startIcon={<X size={16} />}
            >
              Clear Filters
            </Button>
          </Grid2>
        </Grid2>
      </CardContent>
    </Card>
  );
};

export default FilterBar;
